import { t } from "@lingui/core/macro";
import { useTheme } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useRef } from "react";
import { ScrollView } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { VStack, Text } from "@whoosh/design-system/src/components/primitive";

import { useInitializeDiningBookingWizard } from "../../hooks/useInitializeDiningBookingWizard/useInitializeDiningBookingWizard";
import { DiningBookingWizardHeader } from "../DiningBookingWizardHeader/DiningBookingWizardHeader";
import { DiningBookingWizardStepContainer } from "../DiningBookingWizardStepContainer/DiningBookingWizardStepContainer";
import { DiningWizardFooter } from "../DiningWizardFooter/DiningWizardFooter";
import { DiningBookingWizardQueryRendererQuery } from "./__generated__/DiningBookingWizardQueryRendererQuery.graphql";

export const DiningBookingWizardQueryRenderer = ({
  queryRef,
  onClose,
  ownerId,
}: {
  onClose: () => void;
  queryRef: PreloadedQuery<DiningBookingWizardQueryRendererQuery>;
  ownerId: string;
}) => {
  const data = usePreloadedQuery(
    DiningBookingWizardQueryRendererQueryString,
    queryRef
  );
  const { colors } = useTheme();
  const hold = data.diningReservationHold;
  const club = hold?.facility?.club;
  const clubName = hold?.facility?.club?.name ?? "";
  const facility = hold?.facility;
  const dietaryTagOptions =
    facility?.diningDietaryTags?.filter(
      (tag): tag is NonNullable<typeof tag> => !!tag
    ) ?? [];
  const occasionTagOptions =
    facility?.diningOccasionTags?.filter(
      (tag): tag is NonNullable<typeof tag> => !!tag
    ) ?? [];
  const scrollViewRef = useRef<ScrollView>(null);

  useInitializeDiningBookingWizard({
    diningReservationHoldRef: hold,
    ownerId,
    sessionRef: data.session,
  });

  if (!hold) {
    return <DiningReservationHoldErrorState onDismiss={onClose} />;
  }

  return (
    <VStack flex={1}>
      <DiningBookingWizardHeader
        clubName={clubName}
        onClose={onClose}
        club={club}
      />

      <ScrollView
        ref={scrollViewRef}
        style={{ flex: 1, backgroundColor: colors["surface-secondary"] }}
      >
        <DiningBookingWizardStepContainer
          diningDietaryTags={dietaryTagOptions}
          diningOccasionTags={occasionTagOptions}
          startSlotId={hold.startSlot?.id ?? null}
          scrollViewRef={scrollViewRef}
        />
      </ScrollView>
      <DiningWizardFooter
        onClose={onClose}
        expiresAt={hold.expiresAt ?? null}
      />
    </VStack>
  );
};

const DiningReservationHoldErrorState = ({
  onDismiss,
}: {
  onDismiss: () => void;
}) => {
  return (
    <VStack
      flex={1}
      justifyContent="center"
      alignItems="center"
      px={16}
      gap={4}
    >
      <Text textAlign="center">
        {t`There was a problem reserving this time. Please try again.`}
      </Text>
      <Button
        variant="secondary"
        size="small"
        onPress={onDismiss}
        accessibilityLabel={t`Dismiss`}
      >
        {t`Dismiss`}
      </Button>
    </VStack>
  );
};
/**
 * Query for dining bookings
 */
export const DiningBookingWizardQueryRendererQueryString = graphql`
  query DiningBookingWizardQueryRendererQuery($reservationHoldId: ID!) {
    diningReservationHold(id: $reservationHoldId) {
      date
      expiresAt
      partySize
      policy
      roomName
      time
      facility {
        id
        diningDietaryTags
        diningOccasionTags
        club {
          name
          ...ClubLogo_Club
        }
      }
      startSlot {
        id
      }
      ...useInitializeDiningBookingWizard_diningReservationHold
    }
    session {
      ...useInitializeDiningBookingWizard_session
    }
  }
`;
